import Translator from 'components/ErrorTranslator';
import {
  BelgiumValidationContext,
  PaymentOptionEnum,
  PaymentTypeCodeEnum,
  TerminalAcquirer,
  TerminalAcquirerMidTypeEnum,
  TerminalAcquirerTidTypeEnum,
} from 'components/Onboarding/types';
import { beOnlineTerminalId, bePosTerminalId, beTerminalIdUnique } from 'config/schemas/pos';
import { LocationTypeEnum } from 'generated/models';
import * as Yup from 'yup';
import 'config/yupExtensions';
import * as ADDRESS from './address';
import { EMAIL, STRING } from './common';
import { beCompanyId } from './companyId';

const mapOptionToType: Record<PaymentOptionEnum, PaymentTypeCodeEnum[]> = {
  [PaymentOptionEnum.Marketplace]: [PaymentTypeCodeEnum.Marketplace],
  [PaymentOptionEnum.Payconiq]: [PaymentTypeCodeEnum.Payconiq],
  [PaymentOptionEnum.POS]: [PaymentTypeCodeEnum.Terminal],
  [PaymentOptionEnum.PSP]: [PaymentTypeCodeEnum.PSP],
};

const ifThen = (
  requiredType: LocationTypeEnum,
  schema: Yup.Schema,
  thenSchema: Yup.Schema,
  otherwiseSchema?: Yup.Schema,
) =>
  // @ts-expect-error No idea how to fix it
  schema.when('type', {
    is: (type: string) => type === requiredType,
    then: () => thenSchema,
    otherwise: () => otherwiseSchema,
  });

const ifPaymentOption = (option: PaymentOptionEnum, schema: Yup.Schema, thenSchema: Yup.Schema) =>
  schema.when(['paymentOptions', '$isPaymentOptionEnabled'], {
    is: (
      paymentOptions: PaymentOptionEnum[],
      isPaymentOptionEnabled: (code: PaymentTypeCodeEnum) => boolean,
    ) =>
      paymentOptions.includes(option) &&
      mapOptionToType[option].some((t) => isPaymentOptionEnabled(t)),
    then: () => thenSchema,
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAcquirerInfo = (selectedAcquirers: string[], options: any) => {
  const terminalAcquirers = (options?.context?.terminalAcquirers || []) as TerminalAcquirer[];
  const selectedAcquirer = Array.isArray(selectedAcquirers)
    ? selectedAcquirers[0]
    : selectedAcquirers;
  const acquirer = terminalAcquirers.find((a) => a.value === selectedAcquirer);
  return {
    acquirer,
    isMidMandatory: acquirer?.mid === TerminalAcquirerMidTypeEnum.MANDATORY,
    isTidMandatory: acquirer?.tid === TerminalAcquirerTidTypeEnum.MANDATORY,
    isTidUnique: acquirer?.tid_unique,
    midRegEx: acquirer?.mid_validation_rule,
  };
};

export const ifOnlineThen = (
  schema: Yup.Schema,
  thenSchema: Yup.Schema,
  otherwiseSchema?: Yup.Schema,
) => ifThen(LocationTypeEnum.Online, schema, thenSchema, otherwiseSchema);

export const ifPhysicalThen = (
  schema: Yup.Schema,
  thenSchema: Yup.Schema,
  otherwiseSchema?: Yup.Schema,
) => ifThen(LocationTypeEnum.Physical, schema, thenSchema, otherwiseSchema);

export const ifMarketplaceThen = (
  schema: Yup.Schema,
  thenSchema: Yup.Schema,
  otherwiseSchema?: Yup.Schema,
) => ifThen(LocationTypeEnum.Marketplace, schema, thenSchema, otherwiseSchema);

export const WEB = (isMandatory: boolean) =>
  Yup.string()
    .isMandatory(isMandatory)
    .matches(
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
      {
        excludeEmptyString: !isMandatory,
        message: Translator('location_add.form.website.ruleErrorMessage', 'Invalid URL format'),
      },
    );

export const beWebshopEmail = (isMandatory: boolean) =>
  ifPhysicalThen(
    Yup.string(),
    Yup.string().nullable(),
    EMAIL(isMandatory, 'location_add.form.email.ruleErrorMessage'),
  );

// TODO: handle Marketplace
export const beWeb = (isMandatory: boolean) =>
  ifMarketplaceThen(
    Yup.string(),
    Yup.string().nullable(),
    ifOnlineThen(Yup.string(), WEB(isMandatory), WEB(false)),
  );

export const bePhysicalStreetName = (isMandatory: boolean) =>
  ifPhysicalThen(Yup.string(), ADDRESS.streetName(isMandatory));

export const bePhysicalHouseNumber = (isMandatory: boolean) =>
  ifPhysicalThen(Yup.string(), ADDRESS.houseNumber(isMandatory));

export const bePhysicalCity = (isMandatory: boolean) =>
  ifPhysicalThen(Yup.string(), STRING(isMandatory));

export const bePhysicalPostalCode = (isMandatory: boolean) =>
  ifPhysicalThen(Yup.string(), ADDRESS.bePostalCode(isMandatory));

// TODO: refactor
export const beTerminals = (postfix: '' | 'Online') => {
  const requiredPaymentOption =
    postfix === 'Online' ? PaymentOptionEnum.PSP : PaymentOptionEnum.POS;

  return Yup.object().shape({
    [`terminalAcquirer${postfix}`]: ifPaymentOption(
      requiredPaymentOption,
      Yup.string().isMandatory(false),
      Yup.string().isMandatory(true),
    ),
    [`terminalMerchantId${postfix}`]: ifPaymentOption(
      requiredPaymentOption,
      Yup.string(),
      Yup.string().when(
        `terminalAcquirer${postfix}`,
        (
          selectedAcquirers: string[],
          schema: Yup.StringSchema<string, BelgiumValidationContext>,
          options,
        ) => {
          const { acquirer, isMidMandatory, midRegEx } = getAcquirerInfo(
            selectedAcquirers,
            options,
          );

          const resultSchema = schema.isMandatory(isMidMandatory);

          return midRegEx
            ? resultSchema.matches(new RegExp(midRegEx), acquirer?.mid_help_text)
            : resultSchema;
        },
      ),
    ),
    [`POS${postfix}`]: ifPaymentOption(
      requiredPaymentOption,
      Yup.array(), // basic schema
      Yup.array() // extended basic
        .of(
          Yup.object().shape({
            terminalId: Yup.string().when(
              `$terminalAcquirer${postfix}`,
              (
                selectedAcquirers: string[],
                schema: Yup.StringSchema<string, BelgiumValidationContext>,
                options,
              ) => {
                const { isTidMandatory, isTidUnique } = getAcquirerInfo(selectedAcquirers, options);

                const resultSchema =
                  postfix === 'Online'
                    ? beOnlineTerminalId(schema, isTidMandatory)
                    : bePosTerminalId(schema, isTidMandatory);

                return isTidUnique ? beTerminalIdUnique(resultSchema) : resultSchema;
              },
            ),
            terminalProvider: Yup.string().when(
              `$terminalAcquirer${postfix}`,
              (
                selectedAcquirers: string[],
                schema: Yup.StringSchema<string, BelgiumValidationContext>,
                options,
              ) => {
                const { isTidMandatory } = getAcquirerInfo(selectedAcquirers, options);

                return (postfix === 'Online' ? schema.nullable() : schema).isMandatory(
                  isTidMandatory,
                ); // just non-empty string
              },
            ),
          }),
        )
        .isMandatory(
          true,
          Translator(
            'location_add.form.terminalRequiredValidation',
            'Fill at least one payment terminal',
          ),
        )
        .max(
          10,
          Translator(
            'location_add.form.terminalLimitValidation',
            'Only up to 10 terminals is allowed',
          ),
        ),
    ),
  });
};

export const beTerminalDelayDate = (isMandatory: boolean) =>
  ifPhysicalThen(
    Yup.date().nullable(),
    Yup.date()
      .nullable()
      .when('terminalTakeover', {
        is: true,
        then: (dateSchema) =>
          isMandatory
            ? dateSchema.required(
                Translator('global_texts.errorMessages.errorMessageRequired', 'Required field'),
              )
            : dateSchema,
        otherwise: (dateSchema) => dateSchema,
      }),
    Yup.date().nullable(),
  );

export const beTerminalCompanyId = (isMandatory: boolean) =>
  ifPhysicalThen(
    Yup.string(),
    Yup.string().when('terminalTakeover', {
      is: true,
      then: () => beCompanyId(isMandatory),
      otherwise: (schema) => schema.nullable(),
    }),
    Yup.string().nullable(),
  );

export const bePaymentOptions = (isMandatory: boolean) =>
  Yup.array()
    .of(Yup.string())
    .test(
      'Has enabled options',
      Translator(
        'location_add.sectionTexts.paymentOptions.subheader',
        'Select one or multiple payment options for your location',
      ),
      function (value, { options: { context } }) {
        const isPaymentOptionEnabled = (context as BelgiumValidationContext)
          ?.isPaymentOptionEnabled;
        if (isPaymentOptionEnabled && value) {
          return value.some((option) =>
            mapOptionToType[option as PaymentOptionEnum].some((type) =>
              isPaymentOptionEnabled(type),
            ),
          );
        }
        return !isMandatory;
      },
    );

export const acceptedProducts = (isMandatory: boolean) =>
  Yup.array()
    .of(Yup.string())
    .test('At least 1 product is required', function (value, { options: { context } }) {
      if (isMandatory) {
        if (value?.length !== undefined && value.length > 0) return true;
        if (context?.isLocationEdit)
          return this.createError({
            message: Translator(
              'location_add_portal.editPage.form.productRequiredValidationEdit',
              'At least 1 product is required, if you want to deactivate the location, please use the dedicated button below',
            ),
          });
        return this.createError({
          message: Translator(
            'location_add.form.productRequiredValidation',
            'At least 1 product is required',
          ),
        });
      }
      return true;
    });

export const beAcceptedProducts = (isMandatory: boolean) =>
  Yup.array()
    .of(Yup.string())
    .test(
      'Has enabled options',
      Translator(
        'location_add.form.product_required_validation',
        'Please select at least one product',
      ),
      function (value, { options: { context } }) {
        const isProductEnabled = (context as BelgiumValidationContext)?.isProductEnabled;
        if (isProductEnabled && value) {
          return value.some((product) => isProductEnabled(product as string));
        }
        return !isMandatory;
      },
    );
